import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface Props {
  systemId: string;
  allowedRoles: string[];
  children: React.ReactNode;
}

// Super admins (by username) and all admin-role users bypass allowed_systems
const SUPER_ADMINS = ['admin', 'SasiriOCL'];

/**
 * Guards a route by both role AND allowed_systems.
 * Admin-role users and super admins bypass all checks.
 * All other users are restricted by their allowed_systems list.
 * If allowed_systems is empty/null the user has no restriction.
 */
const SystemAccessRoute: React.FC<Props> = ({ systemId, allowedRoles, children }) => {
  const user = useAuthStore((state) => state.user);

  if (!user) return <Navigate to="/login" replace />;

  // Admin role users and super admins always have access to everything
  if (user.role === 'admin' || SUPER_ADMINS.includes(user.username)) {
    return <>{children}</>;
  }

  const hasExplicitSystemAccess = !!(user.allowed_systems && user.allowed_systems.includes(systemId));

  // For non-admin users, explicit allowed_systems grants access even if role list does not include it.
  if (!allowedRoles.includes(user.role) && !hasExplicitSystemAccess) {
    return <Navigate to="/" replace />;
  }

  // All other users: enforce allowed_systems if non-empty
  if (user.allowed_systems && user.allowed_systems.length > 0) {
    if (!user.allowed_systems.includes(systemId)) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

export default SystemAccessRoute;
